/*
 * Health Holding embroidery configurator.
 *
 * Responsibilities, deliberately narrow — the same division of labour as
 * assets/figs-embroidery.js:
 *   - run the modal
 *   - keep the live lockup mockup in step with the choices
 *   - write the hidden line item properties onto the garment's form inputs
 *   - expose getAddonItems() so the existing cart plumbing can add the paid lines
 *
 * It does NOT add anything to the cart itself. sections/figs-product.liquid and
 * assets/drs-embroidery-bridge.js own the two cart paths on this store, and both
 * already call getAddonItems() on the embroidery element. Keeping that contract
 * means the programme needs no new cart code at all.
 *
 * WHAT IS MANDATORY AND WHAT IS NOT
 * The lockup — star mark + the cluster in ARABIC + the same cluster in ENGLISH +
 * شركة الصحة القابضة — is required on every programme garment, so the only gate
 * here is that a cluster has been chosen. A name and an icon are optional
 * additions.
 *
 * GARMENTS WITHOUT A CHEST
 * Trousers, socks, scrub caps and any garment whose type the Liquid could not
 * identify render the silhouette with NO lockup on it, so half the elements this
 * file used to assume are simply absent. Every lockup reference below is therefore
 * guarded. The embroidery is still ordered on those garments — it is only the
 * PLACEMENT that is unknown, and the properties say so rather than naming a side
 * the garment does not have. Nothing is written to the
 * form until confirm, because a disabled input is not submitted and that absence
 * is what assets/drs-hh.js watches to hold Add to Cart.
 */
if (!customElements.get('drs-hh-embroidery')) {
  /* U+0600–U+06FF is the main Arabic block; U+0750–U+077F and U+FB50–U+FDFF cover
     supplements and presentation forms, which phone keyboards do emit. Same
     expression as figs-embroidery.js — a staff name may be typed in either
     script regardless of which storefront locale is being browsed. */
  var HHE_ARABIC = /[؀-ۿݐ-ݿﭐ-﷿]/;

  /* THE TWO THREAD COLOURS. Sampled off Health Holding's own logo, 2026-09-29:
     #1591cf is what both wordmarks — الصحة القابضة and HEALTH HOLDING — are set
     in, and the second colour in the star after its lighter gradient highlight
     (#2baae1). The wordmark's value is the one to stitch: the highlight only
     exists because the star is a gradient, and embroidery here is one thread.

     Named rather than written at each use because they are now read from three
     places — the luminance branch, the no-fabric fallback, and any future one —
     and two of three updated later is the kind of bug that shows up on a single
     product nobody checks.

     PER-PRODUCT OVERRIDE: custom.embroidery_thread_color, read in
     snippets/drs-hh-embroidery.liquid. It is written onto the stage element,
     which wins over everything set here, so a product that must be stitched in
     another colour needs no change to this file. */
  var HHE_THREAD_LIGHT = '#1591cf';   /* on a WHITE garment */
  var HHE_THREAD_DARK  = '#ffffff';   /* on a coloured one */

  /* WHERE "WHITE" STOPS, on the 0..1 luma scale syncFabric() computes.
   *
   * Raised from 0.6 to 0.88, 2026-09-29. Omar: "I want it white if the top has
   * a colour" — blue thread is for the white garments and nothing else.
   *
   * 0.6 was a light/dark split, and it put the pale colours on the wrong side.
   * Measured with the same formula the code uses:
   *
   *     white                 1.00   blue
   *     lab-coat off-white    0.96   blue
   *     ---- 0.88 ----------------------------
   *     Ceil Blue            ~0.71   white
   *     Pewter               ~0.55   white
   *     Sage Green           ~0.53   white
   *     Royal, Navy, Hunter Green, Black   well below, white
   *
   * Ceil Blue is the colour that forced this. At 0.6 it counted as light and
   * took blue thread, which on pale blue fabric is barely a mark. There is a
   * wide gap between it and the off-whites, so 0.88 sits in clear air rather
   * than on top of any real colour in the programme.
   *
   * THE EDGE THIS LEAVES: a cream, ivory or very pale grey — luma above 0.88
   * but not actually white — now takes blue, and something just under it takes
   * white on a nearly white garment. No single threshold avoids both. If such
   * a colour is ever added, set custom.embroidery_thread_color on that product
   * rather than moving this number, which would drag every other colour with it.
   */
  var HHE_WHITE_LUMA = 0.88;

  /* ---- Programme context, remembered for the visit ----------------------
   *
   * `?hh=1` marks a journey that came from the programme. It used to be read
   * from the URL and nothing else, which meant it survived exactly one page:
   * a buyer who arrived from the programme grid and then clicked through to a
   * second garment lost the marker, lost the panel, and could put a programme
   * garment in the cart with no embroidery on it at all. The gate was doing its
   * job to retail shoppers and failing the people it was built for.
   *
   * So arrival is remembered for the rest of the visit. `?hh=1` is still what
   * grants it — nothing else sets this — and every later page in the same tab
   * reads it back.
   *
   * sessionStorage, NOT localStorage, and that is the whole safety of it: the
   * flag dies with the tab. localStorage would mean anyone who ever followed a
   * programme link keeps seeing mandatory SAR 20 embroidery on these 102
   * garments months later while shopping retail, with nothing on screen
   * explaining why and no way to clear it.
   *
   * `?hh=0` clears it, for testing and for a programme buyer who wants to buy
   * one of the same garments plain.
   *
   * Every access is wrapped: storage throws outright in some private-browsing
   * modes, and a panel that cannot remember is the old behaviour, which is
   * survivable — a panel that throws here takes the whole product page's
   * scripts down with it, which is not.
   */
  var HHE_CTX_KEY = 'drs_hh_programme';

  function hheRememberCtx(on) {
    try {
      if (on) sessionStorage.setItem(HHE_CTX_KEY, '1');
      else sessionStorage.removeItem(HHE_CTX_KEY);
    } catch (e) { /* no storage: the URL marker still works on its own page */ }
  }

  function hheRecallCtx() {
    try { return sessionStorage.getItem(HHE_CTX_KEY) === '1'; }
    catch (e) { return false; }
  }

  /* The mark is fetched once per page load, not once per element: a product page
     renders one panel, but a variant change re-renders the section and builds a
     new one, and re-fetching each time would flash an empty mark. */
  var hheMarkPromise = null;

  function hheFetchMark(src) {
    if (hheMarkPromise) return hheMarkPromise;
    hheMarkPromise = fetch(src, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('mark ' + r.status);
        return r.text();
      })
      .then(function (txt) {
        /* Must contain an <svg>. A 404 on the Shopify CDN answers with HTML, and
           injecting that would drop a stray error page into the mockup. */
        if (txt.indexOf('<svg') === -1) throw new Error('mark not an svg');
        return txt;
      });
    return hheMarkPromise;
  }

  /* Drawn only when the real artwork cannot be fetched. A five-point star at the
     same stroke weight as the icons in snippets/figs-embroidery-icon.liquid, so a
     failed fetch still previews the SHAPE and position of the lockup rather than
     collapsing the stack. It is explicitly not a substitute for their mark. */
  var HHE_FALLBACK_STAR =
    '<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.4" ' +
    'stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M24 4l6.2 12.9 14.1 2-10.2 10 2.4 14L24 36.2 11.5 42.9l2.4-14L3.7 18.9l14.1-2z"/></svg>';

  class DrsHhEmbroidery extends HTMLElement {
    connectedCallback() {
      if (!this.inProgrammeContext()) return;

      /* Raw query, not qs(): the helper reads this.modal, which does not exist yet. */
      this.modal = this.querySelector('[data-hhe-modal]');
      this.portal();
      this.hideRetailPanel();

      this.openBtn   = this.qs('[data-hhe-open]');
      this.summaryEl = this.qs('[data-hhe-summary]');
      this.priceEl   = this.qs('[data-hhe-price]');
      this.confirmBtn= this.qs('[data-hhe-confirm]');
      this.select    = this.qs('[data-hhe-cluster-select]');
      this.nameInput = this.qs('[data-hhe-name]');
      /* All five are ABSENT on a garment with no chest — the Liquid renders a
         "placement to be confirmed" badge instead of the lockup. Nothing below may
         assume they exist. */
      this.markEl      = this.qs('[data-hhe-mark]');
      this.clusterEl   = this.qs('[data-hhe-cluster]');
      this.clusterEnEl = this.qs('[data-hhe-cluster-en]');
      this.pnameEl     = this.qs('[data-hhe-pname]');
      this.liconEl     = this.qs('[data-hhe-licon]');
      this.lockupEl    = this.qs('[data-hhe-lockup]');
      this.loupeEl     = this.qs('[data-hhe-loupe]');
      /* WRAPPED, because of WHERE it sits rather than what it does. watchLoupe
         runs before the mockup swap is set up, before the mark is fetched and
         before the open button gets its click listener, so anything it throws
         takes all three with it — and a broken detail card would present as
         "the photo stopped following the colour", nowhere near it. */
      try { this.watchLoupe(); } catch (e) { /* card off, panel intact */ }
      /* '0' means this garment type has no chest panel. Read once, here, so the
         value that decides what goes on the order is read from one place. */
      this.hasChest = this.dataset.chest !== '0';

      /* Cents, straight from the add-on products via the Liquid. Never literals:
         the charge at checkout is the variant price, so a number typed in here
         would be a second source of truth that drifts the moment someone edits a
         price in admin. */
      this.cents = {
        lockup: parseInt(this.dataset.lockupPrice, 10) || 0,
        name:   parseInt(this.dataset.namePrice, 10) || 0,
        icon:   parseInt(this.dataset.iconPrice, 10) || 0
      };

      this.side = this.dataset.side === 'right' ? 'right' : 'left';
      /* clusterAr and clusterEn are the two lines of the lockup; clusterLabel is
         the joined form that goes on the order and into the trigger row. Three
         fields rather than one split later: the join is a display decision and the
         two lines are the data. */
      this.state = { cluster: '', clusterLabel: '', clusterAr: '', clusterEn: '',
                     stitch: '', name: '', icon: '', iconLabel: '' };
      this.applied = false;

      /* Remember the resting labels now, while they are still the resting labels.
         figs-embroidery.js used to reach for a data-idle attribute no element
         carried, so every "restore the idle text" call silently restored the
         CURRENT text and left a stale name on screen after a reset. */
      this.idleSummary = this.summaryEl ? this.summaryEl.textContent : '';
      this.idlePrice   = this.priceEl ? this.priceEl.textContent : '';
      /* The confirm label is a translated sentence with the price inside it, so it
         cannot be rebuilt in JS. Liquid renders it a second time with '%s' where
         the number goes; see data-price-template in the snippet. */
      this.priceTpl = this.confirmBtn ? (this.confirmBtn.dataset.priceTemplate || '') : '';

      /* Per-variant mockups, published by the snippet as JSON. Parsed once here
         rather than on every colour change, and guarded: a malformed block must
         cost the panel its photo swapping, not its whole connectedCallback —
         everything below this line is what makes the modal open at all. */
      this.mockups = null;
      var mockJson = this.qs('[data-hhe-mockups]');
      if (mockJson) {
        try { this.mockups = JSON.parse(mockJson.textContent); }
        catch (e) { this.mockups = null; }
      }
      this.photoEl = this.qs('[data-hhe-photo]');
      this.scheduleMockupSync();

      this.loadMark();

      this.openBtn.addEventListener('click', function () { this.open(); }.bind(this));
      this.qs('[data-hhe-close]').addEventListener('click', function () { this.close(); }.bind(this));
      this.qs('[data-hhe-reset]').addEventListener('click', function () { this.reset(); }.bind(this));
      this.confirmBtn.addEventListener('click', function () { this.apply(); }.bind(this));

      if (this.select) this.select.addEventListener('change', function () {
        var opt = this.select.options[this.select.selectedIndex];
        var on = !!this.select.value;
        this.state.cluster = this.select.value;
        /* The two lockup lines come from the option's data attributes, not from
           splitting its text: the text is a joined bilingual string and a cluster
           name is free to contain the separator. A 4-field legacy row carries no
           English, so clusterEn is empty and that line hides itself. */
        this.state.clusterAr = on ? (opt.dataset.ar || '') : '';
        this.state.clusterEn = on ? (opt.dataset.en || '') : '';
        this.state.clusterLabel = on ? opt.textContent.trim() : '';
        this.state.stitch = on ? (opt.dataset.stitch || '') : '';
        this.render();
      }.bind(this));

      if (this.nameInput) this.nameInput.addEventListener('input', function () {
        this.state.name = this.nameInput.value;
        var c = this.qs('[data-hhe-count]');
        if (c) c.textContent = this.nameInput.value.length + '/26';
        this.render();
      }.bind(this));

      this.on('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-hhe-set-icon]');
        if (!b) return;
        this.state.icon = b.dataset.value || '';
        this.state.iconLabel = this.state.icon ? (b.getAttribute('title') || '') : '';
        this.qsa('[data-hhe-set-icon]').forEach(function (x) {
          x.classList.toggle('is-active', x === b);
        });
        /* Clone the tile's own SVG into the mockup rather than keeping a second
           copy of the artwork here. One source of truth: the icons are rendered
           by snippets/figs-embroidery-icon.liquid and nothing else draws them. */
        var svg = b.querySelector('svg');
        if (this.liconEl) this.liconEl.innerHTML = svg ? svg.outerHTML : '';
        var echo = this.qs('[data-hhe-echo="icon"]');
        if (echo) echo.textContent = this.state.iconLabel;
        this.render();
      }.bind(this));

      /* Escape closes, like any dialog. */
      this.escHandler = function (e) {
        if (e.key === 'Escape' && !this.modal.hidden) this.close();
      }.bind(this);
      document.addEventListener('keydown', this.escHandler);

      /* The theme's variant picker is a set of radios; a colour change fires
         `change` on one of them. Re-reading the fabric then keeps the panel in
         step even while the modal is already open. */
      this.colourHandler = function (e) {
        if (e.target && e.target.classList && e.target.classList.contains('opt-btn')) this.syncFabric();
        /* The mockup is re-read on ANY change in the form, not only on a click
           that matched .opt-btn. syncFabric can afford to be picky because a
           wrong fabric tint is a cosmetic miss; showing the black top's photo
           while the pink one is selected is the panel telling the buyer they
           are about to embroider a garment they did not choose. syncMockup
           compares before it writes, so the extra calls cost nothing. */
        this.scheduleMockupSync();
        /* The fabric tint has the same timing problem, one step milder: the
           swatch's `is-active` class can also be set after this fires. */
        setTimeout(this.syncFabric.bind(this), 60);
      }.bind(this);
      document.addEventListener('change', this.colourHandler);

      /* Themes that announce the change properly are listened to as well. */
      this.variantHandler = function () { this.scheduleMockupSync(); }.bind(this);
      ['variant:change', 'variantChange', 'product:variant-change', 'variant_change']
        .forEach(function (evt) { document.addEventListener(evt, this.variantHandler); }, this);

      this.render();
    }

    /* The same 102 garments sell as ordinary retail, where a mandatory SAR 88
       lockup would be wrong — HEALTH-HOLDING.md: "Scope of that rule: Health
       Holding context only; retail untouched". The panel is rendered by the
       `health-holding` product tag, which cannot tell the two journeys apart, so
       the journey is checked here: `?hh=1`, the marker assets/drs-hh.js already
       sets on every link out of the programme grid.

       REMOVED, not hidden. A hidden panel leaves its properties[…] inputs in the
       product form, and drs-hh.js holds Add to Cart until one of them is enabled —
       a retail shopper would be locked out of a garment they can legitimately buy
       plain. Removing the element takes the inputs with it and the form is exactly
       as it was. */
    inProgrammeContext() {
      if (this.dataset.contextRequired !== '1') return true;

      var hh = new URLSearchParams(window.location.search).get('hh');

      /* Arriving with the marker grants the context AND records it, so the rest
         of the visit keeps the panel without the parameter having to survive
         every link the buyer clicks. */
      if (hh === '1') { hheRememberCtx(true); return true; }

      /* An explicit opt-out beats the remembered flag; anything else falls
         through to what was remembered earlier in this tab. */
      if (hh === '0') hheRememberCtx(false);
      else if (hheRecallCtx()) return true;

      var m = this.querySelector('[data-hhe-modal]');
      if (m) m.remove();
      this.remove();
      return false;
    }

    /* One embroidery flow at a time. A product tagged both `health-holding` and
       `embroidery` renders both panels, and in programme context the FIGS one
       offers a second, optional embroidery beside a mandatory one — two "Add
       embroidery" rows, two prices, no way to tell which is required.

       Hidden rather than removed, the opposite of the decision above and for the
       same reason: figs-embroidery.js has already portalled its own modal and
       bound document-level listeners by now, and tearing its element out from
       under it would leave that modal orphaned on <body>. Its inputs stay
       disabled because nothing can reach its UI to confirm them. */
    hideRetailPanel() {
      var form = this.closest('form');
      if (!form) return;
      form.querySelectorAll('figs-embroidery').forEach(function (el) { el.hidden = true; });
    }

    /* PORTAL — the modal cannot stay where the snippet renders it.
     *
     * On sections/main-product.liquid the panel sits inside .sticky-column-desktop,
     * which is `position: sticky`. Sticky establishes a stacking context on its own,
     * without needing a z-index, so everything inside is painted as a unit at the
     * column's level and a full-screen overlay lands UNDER the site header — its
     * close button beneath the logo, the nav links topmost and clickable through it.
     * It is not a z-index competition: figs-embroidery.js measured that 2147483647
     * changed nothing, and that moving the modal to <body> fixed it immediately.
     * Escaping a stacking context from the inside is not possible at any z-index.
     *
     * The costs of moving, each paid for below:
     *   - delegated listeners bound to this element stop seeing modal events
     *     -> on() binds to both roots
     *   - this.querySelector no longer reaches modal content -> qs()/qsa() search both
     *   - custom properties no longer inherit down -> setVar() sets them on both
     * Nothing in this modal is a named form control, so unlike the FIGS panel there
     * is no upload to re-associate with the form. The hidden property inputs stay
     * behind, inside the product form, which is the point.
     */
    portal() {
      if (!this.modal) return;
      document.body.appendChild(this.modal);
    }

    /* The modal now lives outside this element, so every lookup and every delegated
       listener has to consider both roots. The contains() guard keeps these correct
       if the portal ever did not happen — without it a modal still inside this
       element would be searched twice and fire every handler twice. */
    qs(sel) {
      return this.querySelector(sel)
        || (this.modal && !this.contains(this.modal) ? this.modal.querySelector(sel) : null);
    }

    qsa(sel) {
      var found = Array.prototype.slice.call(this.querySelectorAll(sel));
      if (this.modal && !this.contains(this.modal)) {
        found = found.concat(Array.prototype.slice.call(this.modal.querySelectorAll(sel)));
      }
      return found;
    }

    on(type, fn) {
      this.addEventListener(type, fn);
      if (this.modal && !this.contains(this.modal)) this.modal.addEventListener(type, fn);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.escHandler);
      document.removeEventListener('change', this.colourHandler);
      /* The variant listeners too. The theme disconnects and rebuilds this
         element on every colour change, so one left behind per change would
         accumulate all session and keep a detached element alive with it. */
      if (this.variantHandler) {
        ['variant:change', 'variantChange', 'product:variant-change', 'variant_change']
          .forEach(function (evt) { document.removeEventListener(evt, this.variantHandler); }, this);
      }
      /* The theme re-renders the whole product section on a variant change, which
         disconnects this element. The portalled modal is no longer a child, so it
         would survive that and accumulate one orphan per colour click. */
      if (this.modal && this.modal.parentElement === document.body) this.modal.remove();
      document.body.style.overflow = '';
    }

    /* ---- The mark ---------------------------------------------------- */

    loadMark() {
      var src = this.dataset.markSrc;
      if (!src || !this.markEl) return;
      var el = this.markEl;
      hheFetchMark(src).then(function (svg) {
        el.innerHTML = svg;
        /* The artwork carries its own <title>, which a screen reader would read
           out inside a decorative preview. The wrapper is already aria-hidden;
           strip the title so nothing is announced twice. */
        var t = el.querySelector('title');
        if (t) t.remove();
        this.cropToStar(el);
      }.bind(this)).catch(function () {
        el.classList.add('drs-hhe__mark--fallback');
        el.innerHTML = HHE_FALLBACK_STAR;
      });
    }

    /* Keep the STAR, drop the wordmark.

       assets/drs-hh-logo.svg is the full lockup as the client supplied it: one
       outer <svg> holding three inner ones — the five-point star, then the Arabic
       wordmark الصحة القابضة, then "HEALTH HOLDING" in Latin. That is right for
       the hero and wrong here. The programme's embroidered lockup is star +
       CLUSTER (Arabic) + CLUSTER (English) + شركة الصحة القابضة, so the file's own
       wordmark would sit between the star and the cluster lines and repeat the
       company name — in two languages — two lines above the one that is actually
       stitched. The wordmark being Latin is no longer the objection; the lockup
       carries English now. Repeating the company name is.

       So the wordmark blocks are removed and the outer viewBox is retargeted to
       the star's own box. The numbers are READ OFF the file rather than written
       here: the mark is identified by being the inner <svg> that contains the
       <use> elements, and its x/y/width/height become the viewBox. Re-exporting
       the artwork at a different size therefore needs no change in this file. */
    cropToStar(el) {
      var root = el.querySelector('svg');
      if (!root) return;
      var inners = Array.prototype.slice.call(root.children).filter(function (n) {
        return n.tagName && n.tagName.toLowerCase() === 'svg';
      });
      var mark = inners.filter(function (n) { return n.querySelector('use'); })[0];
      if (!mark) return; // an artwork shape this does not recognise: leave it whole
      inners.forEach(function (n) { if (n !== mark) n.remove(); });

      var x = parseFloat(mark.getAttribute('x')) || 0;
      var y = parseFloat(mark.getAttribute('y')) || 0;
      var w = parseFloat(mark.getAttribute('width'));
      var h = parseFloat(mark.getAttribute('height'));
      if (w && h) root.setAttribute('viewBox', x + ' ' + y + ' ' + w + ' ' + h);
    }

    /* ---- Per-variant mockup ------------------------------------------ */

    /* Point the mockup at the selected variant's own photo.
     *
     * THE VARIANT IS READ OFF THE FORM, not off the swatch the buyer clicked.
     * `[name="id"]` is the input Shopify itself posts to /cart/add, so it is
     * the one field on the page guaranteed to hold the variant actually being
     * bought, whatever the theme's colour picker looks like or how many option
     * dimensions it has. Reading the swatch instead would mean re-deriving the
     * variant from option values — the same answer, worked out a second way,
     * with a second chance of being wrong.
     *
     * A VARIANT WITH NO ENTRY LEAVES THE PHOTO ALONE, deliberately. Missing
     * means the snippet found no variant metafield, so what is on screen is the
     * product-level mockup, which is the correct picture for that variant.
     * Blanking it would turn "this colour has no photo of its own" into "this
     * product has no mockup", and drop the buyer into the vector fallback for
     * a product that has a perfectly good mockup.
     *
     * Called on connect as well as on change: on a theme that re-renders the
     * section the Liquid has already put the right photo there and the compare
     * below makes this a no-op, and on a theme that updates in place it is the
     * only thing that corrects the first paint. */
    syncMockup() {
      if (!this.mockups) return;

      /* Re-found every time rather than trusted from connect: a theme that
         re-renders the product section on a colour change replaces this <img>,
         and the old reference then points at a node no longer on the page.
         Writing to it succeeds silently and nothing moves. */
      var img = this.qs('[data-hhe-photo]');
      if (!img) return;
      this.photoEl = img;

      var form = this.closest('form');
      var input = form && form.querySelector('[name="id"]');
      var id = input && input.value;
      if (!id) return;

      /* A VARIANT WITH NO FILE FALLS BACK TO THE PRODUCT'S OWN PHOTO, and that
         fallback is the fix for the preview sticking on one colour.

         This used to return here and leave whatever was on screen. On the first
         paint that was the product-level photo, so it read as correct. On every
         later change it was the PREVIOUS COLOUR's photo — so choosing a colour
         with its own image and then one without left the first colour's garment
         up, and the preview appeared to stick on the first colour picked.

         Going back to "default" is the honest answer: this colour has no photo
         of its own, so show the generic one, exactly as a reload would. */
      var m = this.mockups[id] || this.mockups['default'];
      if (!m || !m.src) return;

      /* Compare the ATTRIBUTE, not the .src property. The property returns the
         browser's resolved absolute URL — "https://cdn.shopify.com/…" — while
         Liquid's image_url gives a protocol-relative "//cdn.shopify.com/…", so
         property-against-Liquid never matched and this guard never fired. */
      if (img.getAttribute('src') === m.src) return;

      img.setAttribute('src', m.src);
      /* srcset is ALWAYS rewritten, never left alone. A browser that has a
         srcset picks from it and ignores src entirely, so a stale srcset from
         the previous colour would keep displaying the previous garment no
         matter what src said. */
      img.setAttribute('srcset', m.src2x ? (m.src + ' 800w, ' + m.src2x + ' 1200w')
                                         : (m.src + ' 800w'));
    }

    /* WHEN to re-read the variant, which is the harder half.
     *
     * syncMockup reads `[name="id"]`, and the theme writes that field in its own
     * `change` handler. Two listeners on one event run in the order they were
     * added, which is not ours to decide — so reading it the instant the event
     * fires can read the value from BEFORE the click, and the photo lags one
     * colour behind. Themes also differ in WHEN they write it: some
     * synchronously, some after a fetch that re-renders the section.
     *
     * So this checks a few times across the first second instead of betting on
     * one delay that suits one theme. Safe to call as often as anything likes:
     * syncMockup compares before it writes. */
    scheduleMockupSync() {
      if (!this.mockups) return;
      var self = this;
      [0, 60, 160, 400, 900].forEach(function (ms) {
        setTimeout(function () { self.syncMockup(); }, ms);
      });
    }

    /* ---- Fabric and thread colour ------------------------------------ */

    open() {
      this.syncFabric();
      this.syncMockup();
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      if (this.select) this.select.focus();
    }

    close() {
      this.modal.hidden = true;
      document.body.style.overflow = '';
    }

    /* Tint the panel with the colourway actually selected, so the lockup is
       previewed against the fabric being bought rather than a guess. The colour is
       read off the live swatch instead of duplicated here — the swatch gets it from
       --swatch-background-color, set per colour in snippets/drs-swatch-styles.liquid,
       so reading the computed value keeps one source of truth.

       THREE SELECTORS, because this store renders colour three ways:
         - sections/figs-product.liquid  -> .figs-pdp__swatch.is-active > span
         - sections/main-product.liquid  -> the theme's variant picker: a hidden
           radio followed by a label carrying the custom property
         - assets/drs-colour-strip.js    -> [data-colour-url].is-active
       Many programme products have no colour option at all (the HH_ATHL_TRIAL
       pair have a single "Default Title" variant), so returning empty and letting
       the stylesheet's neutral stand is a normal outcome, not a failure. */
    syncFabric() {
      var fabric = '';

      var sw = document.querySelector('.figs-pdp__swatch.is-active span');
      if (sw) {
        fabric = getComputedStyle(sw).backgroundColor;
      } else {
        var lb = document.querySelector('.opt-btn:checked + .opt-label--swatch')
              || document.querySelector('[data-colour-url].is-active');
        if (lb) {
          var cs = getComputedStyle(lb);
          fabric = (cs.getPropertyValue('--swatch-background-color') || '').trim() || cs.backgroundColor;
        }
      }

      /* Lab coats usually carry no colour option at all, so there is no swatch to
         read. Without a default the probe below never runs, the thread keeps its
         white fallback, and the lockup vanishes into a white coat. The Liquid
         supplies the garment's own colour for exactly that case. */
      if (!fabric || fabric === 'rgba(0, 0, 0, 0)' || fabric === 'transparent') {
        fabric = this.dataset.defaultFabric || '';
      }
      /* STILL NOTHING TO READ. Changed 2026-09-29: this used to `return` and
         leave --hhe-thread unset, which the stylesheet then resolved to its
         white fallback. That was survivable while the preview was a vector
         silhouette, because the silhouette was tinted by the same missing
         value and the two failed together, visibly. It is not survivable now
         that the preview is a real PHOTO: the photo has a colour of its own
         that no swatch reports, so a white-coat mockup on a product with no
         colour option rendered a white lockup on a white garment — nothing at
         all, and nothing on screen to say why.

         So the unknown case falls back to the programme's blue rather than to
         white. It is the safer of the two by a distance: against black it
         carries about 6:1, against white about 3.5:1, so it is legible either
         way, where white is legible on exactly one of them. */
      if (!fabric) {
        this.setVar('--hhe-thread', HHE_THREAD_LIGHT);
        return;
      }
      /* Set on the modal as well as the host: the panel reads these custom
         properties by inheritance, and portal() moved it out of this tree. */
      this.setVar('--hhe-fabric', fabric);

      /* Thread colour is NOT a buyer choice — the programme specifies the lockup,
         and offering a colour would let someone order a non-conforming uniform.
         But a preview has to be legible on a white lab coat and on navy alike, so
         it is derived from the fabric's luminance and shown, not asked.
         (Flagged for the client: if Health Holding mandate one thread colour,
         replace this with that colour and delete the probe.)

         The two colour sources use different notations — the theme's swatches
         compute to `rgb(27 73 100)` while drs-swatch-styles writes plain hex — so
         digit-scraping the raw string reads a hex as garbage. Hand the value to
         the browser and read back its normalised rgb() rather than writing a
         parser per notation. The probe must be in the document to compute. */
      var probe = document.createElement('span');
      probe.style.cssText = 'display:none;color:' + fabric;
      this.appendChild(probe);
      var rgb = getComputedStyle(probe).color;
      probe.remove();

      var m = rgb.match(/\d+/g);
      if (m) {
        var lum = (0.299 * m[0] + 0.587 * m[1] + 0.114 * m[2]) / 255;
        /* `white` rather than `light`: the question this answers is no longer
           "is the fabric light" but "is the fabric white", and the variable
           below still drives the seam and edge colours, which want the same
           answer — a drawing of a white coat needs dark lines. */
        var white = lum > HHE_WHITE_LUMA;
        /* Two thread colours, and the fabric picks between them: the
           programme's blue on a WHITE garment, white on every colour. Omar,
           2026-09-29, in two steps — first "everything will be white except
           the white ones, which will be this colour", with the brand logo as
           the reference, then "I want it white if the top has a colour" once
           Ceil Blue turned out to be counting as light.

           Both halves of this line have moved since Moose set it on
           2026-09-23. The colour: navy #181c54 became the wordmark blue. The
           rule: it used to be light-vs-dark, and is now white-vs-coloured —
           see HHE_WHITE_LUMA for where that line is drawn and why. */
        this.setVar('--hhe-thread', white ? HHE_THREAD_LIGHT : HHE_THREAD_DARK);
        /* The technical flats' seam and topstitch colour (assets/drs-hh-garments.css):
           navy on light fabric, white on dark — the same flip as the thread, so the
           drawing always reads and never competes with the lockup. */
        this.setVar('--hhe-line', white ? 'rgba(27, 34, 64, .78)' : 'rgba(255, 255, 255, .8)');
        /* The garment's own seams, hems and pockets, drawn by
           snippets/drs-hh-garments.liquid. They flip on the SAME luminance test as
           the thread and for the same reason: a white lab coat needs dark seams and
           a navy scrub top needs light ones, so neither can be a literal. Muted, so
           the silhouette reads as a garment behind the lockup rather than competing
           with it. */
        this.setVar('--hhe-edge', white ? 'rgba(20, 32, 48, 0.42)' : 'rgba(255, 255, 255, 0.5)');
      }
    }

    /* The detail inset is a COPY of the real lockup, remade whenever the lockup
       changes — a cluster, a name, an icon, or the star arriving from its fetch.
       Copied rather than rendered twice so the magnified version can never say
       something the true-size one does not. Hook attributes are stripped from the
       copy so qs() can never pick it up in place of the original. */
    watchLoupe() {
      if (!this.loupeEl || !this.lockupEl || !window.MutationObserver) return;
      var self = this, pending = false;

      var strip = function (node) {
        [node].concat([].slice.call(node.querySelectorAll('*'))).forEach(function (n) {
          [].slice.call(n.attributes).forEach(function (a) {
            if (a.name.indexOf('data-hhe') === 0) n.removeAttribute(a.name);
          });
        });
        return node;
      };

      /* THE NAME IS COPIED SEPARATELY when it is not inside the lockup. Since
         2026-10-01 it is embroidered on the opposite chest, so it is no longer
         a row of the lockup to be cloned with it — and it is the single most
         likely thing on the order to be misspelled, which is the whole reason
         this magnified card exists. In vector mode the two are still one stack
         and this test sees that, rather than printing the name twice. */
      var nameIsSeparate = function () {
        return self.pnameEl && !self.lockupEl.contains(self.pnameEl);
      };

      var copy = function () {
        pending = false;
        var parts = [strip(self.lockupEl.cloneNode(true))];
        if (nameIsSeparate() && !self.pnameEl.hidden) {
          parts.push(strip(self.pnameEl.cloneNode(true)));
        }
        var label = document.createElement('span');
        label.className = 'drs-hhe__loupe-label';
        label.textContent = self.loupeEl.getAttribute('data-label') || '';
        parts.push(label);
        self.loupeEl.replaceChildren.apply(self.loupeEl, parts);
      };

      var schedule = function () { if (!pending) { pending = true; requestAnimationFrame(copy); } };
      var opts = { childList: true, subtree: true, characterData: true, attributes: true };

      new MutationObserver(schedule).observe(this.lockupEl, opts);
      /* A second observer rather than one on a shared ancestor: the two now sit
         in different positioned wrappers, and the nearest element containing
         both is the photo wrapper — where every unrelated attribute change on
         the image would also wake this up. */
      if (nameIsSeparate()) new MutationObserver(schedule).observe(this.pnameEl, opts);

      schedule();
    }

    setVar(name, value) {
      this.style.setProperty(name, value);
      if (this.modal) this.modal.style.setProperty(name, value);
    }

    /* ---- Rendering --------------------------------------------------- */

    render() {
      var s = this.state;

      /* The one gate. A cluster is the only mandatory choice, so it is the only
         thing that can make the panel confirmable. */
      this.confirmBtn.disabled = !s.cluster;

      /* The two lockup lines. Absent entirely on a garment with no chest, where
         the Liquid renders the placement notice in their place. */
      if (this.clusterEl) this.clusterEl.textContent = s.clusterAr;
      if (this.clusterEnEl) {
        this.clusterEnEl.textContent = s.clusterEn;
        /* Hidden rather than left empty: an empty flex child still takes its gap,
           which opens a visible hole between the Arabic line and the company line
           on a cluster whose row carries no English. */
        this.clusterEnEl.hidden = !s.clusterEn;
      }

      var hasName = s.name.trim().length > 0;
      if (this.pnameEl) {
        this.pnameEl.hidden = !hasName;
        this.pnameEl.textContent = s.name;
      }
      /* Script chosen by what was typed, not by the storefront locale: an Arabic
         speaker browsing the English store still wants her own name stitched in
         Arabic, and an English name inside an Arabic lockup is normal here. */
      var nameArabic = HHE_ARABIC.test(s.name);
      if (this.pnameEl) {
        this.pnameEl.dir = nameArabic ? 'rtl' : 'ltr';
        /* Same family either way: Cairo carries both scripts, so a name matches the
           lockup above it whichever script it was typed in. */
        this.pnameEl.style.fontFamily = "'Cairo','Noto Kufi Arabic','Geeza Pro','Helvetica Neue',Arial,sans-serif";
      }

      if (this.liconEl) this.liconEl.hidden = !s.icon;

      /* Total on the confirm button. The lockup is always in it — that is what
         "mandatory" costs — so an untouched panel already quotes the real price. */
      var cents = this.cents.lockup
        + (hasName ? this.cents.name : 0)
        + (s.icon ? this.cents.icon : 0);
      if (this.priceTpl) {
        this.confirmBtn.textContent = this.priceTpl.replace('%s', this.formatCents(cents));
      }
      this.pendingCents = cents;
    }

    /* ---- Confirm / reset --------------------------------------------- */

    apply() {
      var s = this.state;
      if (!s.cluster) return;

      var hasName = s.name.trim().length > 0;

      /* EVERY PIECE NAMES ITS OWN PLACEMENT (2026-10-01). The three are
         embroidered in three different places — lockup on one chest, name on
         the other, icon on a sleeve — so a summary carrying one side would be
         describing a garment nobody is going to stitch.

         Both labels fall back to the lockup's side if the snippet serving this
         page is an older one that does not publish them; the two files are
         meant to ship together, and a wrong side reads better than a blank one
         to whoever stitches it. */
      var nameSide = this.dataset.nameSideLabel || this.dataset.sideLabel;
      var iconSide = this.dataset.iconSideLabel || this.dataset.sideLabel;

      var summary = [
        'Health Holding lockup — ' + this.dataset.sideLabel,
        s.clusterLabel,
        hasName ? 'Name: ' + s.name.trim() + ' — ' + nameSide : '',
        s.icon ? 'Icon: ' + s.iconLabel + ' — ' + iconSide : ''
      ].filter(Boolean).join(' · ');

      /* Customer-visible properties. */
      this.setProp('summary', summary);
      this.setProp('cluster', s.clusterLabel);
      this.setProp('name', hasName ? s.name.trim() : '');
      this.setProp('icon', s.icon ? s.iconLabel : '');
      this.setProp('side', this.dataset.sideLabel);
      /* Written only when there is something to place: setProp disables an
         input given an empty value, and a disabled input is not submitted. An
         order with no staff name therefore carries no name placement either. */
      this.setProp('nameside', hasName ? nameSide : '');
      this.setProp('iconside', s.icon ? iconSide : '');

      /* Underscore-prefixed: on the order, hidden from the customer.
         `_hh_programme` is the marker the Validation Function will key on.

         `_hh_side` is 'tbc' on a garment with no chest panel. It is NOT 'left' —
         this value is read by a human loading a stitch file, and a pair of joggers
         stamped 'left' costs a remake. The customer-visible placement above
         already says "to be confirmed", set server-side in the Liquid; this is the
         machine-readable half of the same statement. Anything downstream that
         switches on left/right must treat 'tbc' as "ask the buyer", never as a
         default side. */
      this.setProp('programme', 'health-holding');
      this.setProp('clusterkey', s.cluster);
      this.setProp('stitch', s.stitch);
      this.setProp('sidekey', this.hasChest ? this.side : 'tbc');

      this.applied = true;
      this.refreshRow();
      this.close();
    }

    reset() {
      this.state = { cluster: '', clusterLabel: '', clusterAr: '', clusterEn: '',
                     stitch: '', name: '', icon: '', iconLabel: '' };
      this.applied = false;
      if (this.select) this.select.value = '';
      if (this.nameInput) this.nameInput.value = '';
      var c = this.qs('[data-hhe-count]');
      if (c) c.textContent = '0/26';
      this.qsa('[data-hhe-set-icon]').forEach(function (x, i) {
        x.classList.toggle('is-active', i === 0); // the explicit "no icon" tile
      });
      if (this.liconEl) this.liconEl.innerHTML = '';
      var echo = this.qs('[data-hhe-echo="icon"]');
      if (echo) echo.textContent = '';
      /* Clearing the property inputs is what un-confirms the order: a disabled
         input is not submitted, and assets/drs-hh.js reads that absence as "no
         embroidery yet" and holds Add to Cart again. */
      this.qsa('[data-emb-prop]').forEach(function (i) { i.value = ''; i.disabled = true; });
      this.refreshRow();
      this.render();
    }

    /* Disabled inputs are not submitted, which is what keeps an unconfirmed panel
       from putting empty properties on the order. */
    setProp(name, value) {
      var el = this.qs('[data-emb-prop="' + name + '"]');
      if (!el) return;
      el.value = value || '';
      el.disabled = !value;
    }

    refreshRow() {
      if (!this.applied) {
        this.summaryEl.textContent = this.idleSummary;
        this.priceEl.textContent = this.idlePrice;
        this.openBtn.classList.remove('is-set');
        return;
      }
      var bits = [this.state.clusterLabel];
      if (this.state.name.trim()) bits.push(this.state.name.trim());
      if (this.state.icon) bits.push(this.state.iconLabel);
      this.summaryEl.textContent = bits.join(' · ');
      this.priceEl.textContent = '+' + this.dataset.currency + ' ' + this.formatCents(this.pendingCents);
      this.openBtn.classList.add('is-set');
    }

    /* Whole riyals stay whole; halalas survive if a price ever has them. */
    formatCents(cents) {
      var v = cents / 100;
      return v % 1 === 0 ? String(v) : v.toFixed(2);
    }

    /* ---- The cart contract ------------------------------------------- */

    /* There is no file upload in this flow, so the fast JSON cart route always
       applies. Defined anyway because sections/figs-product.liquid and
       assets/drs-embroidery-bridge.js both call it when it exists. */
    needsNativeSubmit() { return false; }

    /* Called by sections/figs-product.liquid and assets/drs-embroidery-bridge.js.
       Returns the extra cart lines this purchase needs — the same contract as
       figs-embroidery.js, so neither caller needs new code.

       `_hh_programme` is deliberately NOT stamped on these lines. The Validation
       Function will refuse any line carrying that marker without embroidery, and
       these ARE the embroidery — marking them too would make each one demand an
       embroidery line of its own. `_for` pairs them with the garment, which is the
       convention already in use on this store. */
    getAddonItems() {
      var items = [];
      if (!this.applied) return items;
      var forProp = this.dataset.productTitle;
      var s = this.state;

      if (this.dataset.lockupVariant) {
        items.push({ id: parseInt(this.dataset.lockupVariant, 10), quantity: 1, properties: {
          '_for': forProp,
          /* clusterLabel is the joined bilingual string, which is what the
             embroidery operator wants on the line: one field naming the cluster in
             both the language of the artwork and the language of the file index. */
          'Embroidery': 'Health Holding lockup — ' + s.clusterLabel,
          '_hh_addon': 'lockup',
          '_hh_stitch_file': s.stitch
        } });
      }
      if (s.name.trim() && this.dataset.nameVariant) {
        items.push({ id: parseInt(this.dataset.nameVariant, 10), quantity: 1, properties: {
          '_for': forProp,
          'Embroidery': 'Name: ' + s.name.trim(),
          '_hh_addon': 'name'
        } });
      }
      if (s.icon && this.dataset.iconVariant) {
        items.push({ id: parseInt(this.dataset.iconVariant, 10), quantity: 1, properties: {
          '_for': forProp,
          'Embroidery': 'Icon: ' + s.iconLabel,
          '_hh_addon': 'icon'
        } });
      }
      return items;
    }
  }

  customElements.define('drs-hh-embroidery', DrsHhEmbroidery);
}